import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../services/api";
import { useAuthStore } from "../store/useAuthStore";
import { useNotificationBadgeStore } from "../store/useNotificationBadgeStore";

export interface OpenNotification {
  id?: number | string;
  title: string;
  notification: string;
  content?: string;
  type: "success" | "error" | "warning" | "message" | string;
  read?: boolean;
  schema?: string;
  idSchema?: string;
  inviteId?: string;
  inviteType?: string;
  path?: string;
}

type NotificationContextData = {
  open: boolean;
  title: string;
  content: string;
  type: "success" | "error" | "warning";
  unreadCount: number;
  notifications: OpenNotification[];
  setOpenNotification(data: OpenNotification): void;
  markAllAsRead(): void;
  refreshNotifications(): Promise<void>;
};

type NotificationProviderProps = {
  children: ReactNode;
};

function normalizeNotification(raw: any): OpenNotification | null {
  if (!raw) return null;
  const body = raw.content ?? raw.notification ?? raw.message ?? "";
  const title = raw.title || "Notificação";
  if (!title && !body) return null;

  return {
    id: raw.id,
    title,
    notification: body,
    content: body,
    type: raw.type || "success",
    read: Boolean(raw.read ?? raw.isRead),
    schema: raw.schema,
    idSchema: raw.idSchema,
    inviteId: raw.inviteId || (raw.schema === "PRIVATE_INVITE" ? raw.idSchema : undefined),
    inviteType: raw.inviteType,
    path: raw.path,
  } as OpenNotification;
}

function persistToIndexedDB(items: OpenNotification[]) {
  try {
    const request = indexedDB.open("notificationsDB", 1);
    request.onupgradeneeded = (event: any) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains("notifications")) {
        db.createObjectStore("notifications", { keyPath: "id", autoIncrement: true });
      }
    };
    request.onsuccess = (event: any) => {
      try {
        const db = event.target.result;
        const transaction = db.transaction("notifications", "readwrite");
        const store = transaction.objectStore("notifications");
        for (const n of items) {
          if (n.id == null) continue;
          store.put({
            id: n.id,
            title: n.title,
            notification: n.notification,
            content: n.content ?? n.notification,
            type: n.type,
            read: !!n.read,
            schema: n.schema,
            idSchema: n.idSchema,
            inviteId: n.inviteId,
            inviteType: n.inviteType,
            path: n.path,
          });
        }
      } catch (_) {
        /* contexto restrito */
      }
    };
  } catch (_) {
    /* indexedDB indisponivel */
  }
}

export const NotificationContext = createContext({} as NotificationContextData);

export function NotificationProvider({ children }: NotificationProviderProps) {
  const userId = useAuthStore((s) => s.user?.id);
  const setBadgeUnread = useNotificationBadgeStore((s) => s.setUnreadCount);

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [type, setType] = useState<"success" | "error" | "warning">("success");
  const [notifications, setNotifications] = useState<OpenNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const applyUnread = useCallback(
    (count: number) => {
      const n = Math.max(0, Math.min(99, Math.floor(count)));
      setUnreadCount(n);
      // App.tsx soma com mensagens não lidas e aplica setAppBadge + SW UPDATE_BADGE
      setBadgeUnread(n);
    },
    [setBadgeUnread],
  );

  const mergeNotifications = useCallback(
    (incoming: OpenNotification[]) => {
      setNotifications((prev) => {
        const byId = new Map<string, OpenNotification>();
        for (const n of prev) {
          if (n.id != null) byId.set(String(n.id), n);
        }
        for (const n of incoming) {
          if (n.id == null) continue;
          const key = String(n.id);
          const existing = byId.get(key);
          byId.set(key, existing ? { ...existing, ...n } : n);
        }
        const withoutId = prev.filter((n) => n.id == null);
        const merged = [...withoutId, ...Array.from(byId.values())];
        merged.sort((a, b) => {
          // Prefer unread first, then id
          if (!!a.read !== !!b.read) return a.read ? 1 : -1;
          return String(b.id ?? "").localeCompare(String(a.id ?? ""));
        });
        const unread = merged.filter((n) => !n.read).length;
        applyUnread(unread);
        return merged;
      });
    },
    [applyUnread],
  );

  const loadNotificationsFromDB = useCallback(() => {
    try {
      const request = indexedDB.open("notificationsDB", 1);

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains("notifications")) {
          db.createObjectStore("notifications", { keyPath: "id", autoIncrement: true });
        }
      };

      request.onsuccess = (event: any) => {
        try {
          const db = event.target.result;
          const transaction = db.transaction("notifications", "readonly");
          const store = transaction.objectStore("notifications");
          const getAll = store.getAll();

          getAll.onsuccess = () => {
            const allNotifications = (getAll.result || [])
              .map((n: any) => normalizeNotification(n))
              .filter(Boolean) as OpenNotification[];
            if (allNotifications.length) mergeNotifications(allNotifications);
          };
        } catch (_) {
          /* IndexedDB pode falhar em contextos restritos */
        }
      };

      request.onerror = () => {};
    } catch (_) {
      /* indexedDB indisponivel */
    }
  }, [mergeNotifications]);

  const refreshNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem("@backoffice:token");
      if (!token) return;
      const { data } = await api.get("/notifications");
      const list = (data?.notifications || [])
        .map((n: any) => normalizeNotification(n))
        .filter(Boolean) as OpenNotification[];
      const serverUnread =
        typeof data?.quantity === "number"
          ? data.quantity
          : list.filter((n) => !n.read).length;
      console.log("[Notif] API notifications:", list.length, "unread:", serverUnread);

      // API is source of truth for persisted rows; keep ephemeral (no id) toasts
      setNotifications((prev) => {
        const ephemeral = prev.filter((n) => n.id == null);
        return [...list, ...ephemeral];
      });
      applyUnread(serverUnread);
      persistToIndexedDB(list);
    } catch (err) {
      console.warn("[Notif] Falha ao carregar /notifications", err);
    }
  }, [applyUnread]);

  // Hydrate when user logs in / switches account
  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      applyUnread(0);
      return;
    }
    loadNotificationsFromDB();
    void refreshNotifications();
  }, [userId, loadNotificationsFromDB, refreshNotifications, applyUnread]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && userId) {
        void refreshNotifications();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [refreshNotifications, userId]);

  const setOpenNotification = useCallback(
    (raw: OpenNotification) => {
      const normalized = normalizeNotification(raw);
      if (!normalized) return;

      const body = normalized.notification || normalized.content || "";

      setNotifications((prev) => {
        const alreadyExists =
          normalized.id != null && prev.some((n) => String(n.id) === String(normalized.id));
        if (alreadyExists) return prev;

        setTitle(normalized.title);
        setContent(body);
        setType((normalized.type as "success" | "error" | "warning") || "success");
        const nextUnread = prev.filter((n) => !n.read).length + 1;
        applyUnread(nextUnread);
        setOpen(true);
        persistToIndexedDB([{ ...normalized, notification: body, content: body, read: false }]);
        setTimeout(() => setOpen(false), 5000);

        return [...prev, { ...normalized, notification: body, content: body, read: false }];
      });
    },
    [applyUnread],
  );

  function markAllAsRead() {
    applyUnread(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));

    try {
      const request = indexedDB.open("notificationsDB", 1);
      request.onsuccess = (event: any) => {
        try {
          const db = event.target.result;
          const transaction = db.transaction("notifications", "readwrite");
          const store = transaction.objectStore("notifications");

          store.getAll().onsuccess = (e: any) => {
            e.target.result.forEach((n: any) => {
              n.read = true;
              store.put(n);
            });
          };
        } catch (_) {
          /* iOS private browsing / contexto restrito */
        }
      };
      request.onerror = () => {};
    } catch (_) {
      /* indexedDB indisponivel */
    }

    api.get("/notifications/read").catch(() => {});
  }

  return (
    <NotificationContext.Provider
      value={{
        open,
        title,
        content,
        type,
        notifications,
        unreadCount,
        setOpenNotification,
        markAllAsRead,
        refreshNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  return useContext(NotificationContext);
}
